import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MdPhone, MdArrowForward } from "react-icons/md";
import { useAuth } from "../context/AuthContext";

export default function LoginPage() {
  const navigate = useNavigate();
  const { user, sendOtp, verifyOtp, showToast } = useAuth();
  const [step, setStep]           = useState("phone");
  const [phone, setPhone]         = useState("");
  const [otp, setOtp]             = useState(["","","","","",""]);
  const [loading, setLoading]     = useState(false);
  const [agreed, setAgreed]       = useState(false);
  const [agreeErr, setAgreeErr]   = useState(false);
  const refs = useRef([]);

  useEffect(() => { if (user) navigate("/"); }, [user, navigate]);

  const doSend = async (e) => {
    e.preventDefault();
    if (phone.replace(/\D/g, "").length < 10) {
      showToast("Enter valid 10-digit number", "error"); return;
    }
    if (!agreed) {
      setAgreeErr(true);
      showToast("Please accept Terms & Conditions to continue", "error");
      return;
    }
    setAgreeErr(false);
    setLoading(true);
    const result = await sendOtp(phone);
    setLoading(false);
    // ── Check if OTP actually sent successfully ──
    if (result?.error) {
      showToast(result.error || "Failed to send OTP. Please try again.", "error");
      return;
    }
    showToast("OTP sent to +91" + phone.replace(/\D/g, ""), "success");
    setStep("otp");
    setTimeout(() => refs.current[0]?.focus(), 100);
  };

  const oChange = (i, v) => {
    const d = v.replace(/\D/g, "");
    if (!d && v) return;
    const n = [...otp];
    if (d.length > 1) {
      const a = d.slice(0, 6).split("");
      setOtp([...a, ...Array(6 - a.length).fill("")]);
      refs.current[Math.min(5, a.length - 1)]?.focus();
      return;
    }
    n[i] = d; setOtp(n);
    if (d && i < 5) refs.current[i + 1]?.focus();
  };

  const oKey = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) refs.current[i - 1]?.focus();
  };

  const doVerify = async (e) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length < 6) { showToast("Enter the 6-digit OTP", "error"); return; }
    setLoading(true);
    const r = await verifyOtp(phone, code);
    setLoading(false);
    if (r && !r.error) { showToast("Welcome to HCEN! 🎉", "success"); navigate("/"); }
    else if (r?.error) { showToast(r.error || "Invalid OTP", "error"); }
  };

  return (
    <div className="fade-in" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div className="glass" style={{ width: "100%", maxWidth: 420, padding: "36px 28px" }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>🏥</div>
          <div style={{ fontSize: 26, fontWeight: 900, background: "linear-gradient(135deg,var(--red),var(--red-light))", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>HCEN</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>Healthcare Emergency Network</div>
        </div>

        {/* ── PHONE STEP ── */}
        {step === "phone" ? (
          <form onSubmit={doSend}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4, color: "#1a1a2e" }}>Login / Sign Up</div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 22 }}>Enter your phone number to receive OTP</div>

            {/* Phone input */}
            <div className="input-group">
              <label className="input-label">Phone Number *</label>
              <div style={{ display: "flex", gap: 8 }}>
                <div className="input" style={{ width: 60, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, background: "#fff5f5", color: "var(--red)" }}>+91</div>
                <input className="input" type="tel" placeholder="10-digit number"
                  value={phone}
                  onChange={e => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  maxLength={10} style={{ flex: 1 }} required />
              </div>
            </div>

            {/* ── Terms & Conditions Checkbox ── */}
            <div style={{ marginBottom: 20 }}>
              <label
                onClick={() => { setAgreed(p => !p); setAgreeErr(false); }}
                style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer", userSelect: "none" }}
              >
                {/* Animated checkbox */}
                <div style={{
                  width: 22, height: 22, minWidth: 22,
                  borderRadius: 6,
                  border: agreeErr
                    ? "2px solid #d32f2f"
                    : agreed
                      ? "2px solid #2e7d32"
                      : "2px solid #ddd",
                  background: agreed ? "rgba(46,125,50,0.1)" : "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.2s ease",
                  marginTop: 1,
                  boxShadow: agreeErr ? "0 0 0 3px rgba(211,47,47,0.12)" : "none",
                }}>
                  {agreed && (
                    <svg width="13" height="10" viewBox="0 0 13 10" fill="none">
                      <path d="M1 5L5 9L12 1" stroke="#2e7d32" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>

                <span style={{ fontSize: 13, color: agreeErr ? "#d32f2f" : "#444", lineHeight: 1.6 }}>
                  I have read and agree to the{" "}
                  <Link to="/terms" target="_blank"
                    onClick={e => e.stopPropagation()}
                    style={{ color: "#d32f2f", fontWeight: 700, textDecoration: "underline" }}>
                    Terms & Conditions
                  </Link>
                  {" "}and{" "}
                  <Link to="/privacy" target="_blank"
                    onClick={e => e.stopPropagation()}
                    style={{ color: "#d32f2f", fontWeight: 700, textDecoration: "underline" }}>
                    Privacy Policy
                  </Link>
                  {" "}of HCEN.
                </span>
              </label>

              {agreeErr && (
                <div style={{ marginTop: 7, marginLeft: 34, fontSize: 12, color: "#d32f2f", display: "flex", alignItems: "center", gap: 4 }}>
                  ⚠ Please accept the Terms & Conditions to continue.
                </div>
              )}
            </div>

            {/* Send OTP button */}
            <button type="submit" className="btn btn-primary btn-full btn-lg"
              disabled={loading}
              style={{ opacity: !agreed ? 0.55 : 1, cursor: !agreed ? "not-allowed" : "pointer" }}>
              {loading ? "⏳ Sending…" : <><MdPhone /> Send OTP</>}
            </button>
          </form>

        ) : (
          /* ── OTP STEP ── */
          <form onSubmit={doVerify}>
            <button type="button" onClick={() => setStep("phone")}
              style={{ background: "none", border: "none", color: "var(--text-muted)", fontSize: 13, cursor: "pointer", marginBottom: 16, padding: 0 }}>
              ← Change number
            </button>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4, color: "#1a1a2e" }}>Enter OTP</div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 24 }}>
              6-digit code sent to <strong>+91{phone}</strong>
            </div>
            <div className="otp-inputs" style={{ marginBottom: 28 }}>
              {otp.map((d, i) => (
                <input key={i} ref={el => refs.current[i] = el}
                  className="otp-digit" type="tel" maxLength={6} inputMode="numeric"
                  value={d}
                  onChange={e => oChange(i, e.target.value)}
                  onKeyDown={e => oKey(i, e)} />
              ))}
            </div>
            <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading}>
              {loading ? "⏳ Verifying…" : <><MdArrowForward /> Verify &amp; Login</>}
            </button>
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <button type="button" onClick={doSend}
                style={{ background: "none", border: "none", color: "var(--red)", fontSize: 13, cursor: "pointer" }}>
                Resend OTP
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
